
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  convertRequestErrorToMap,
  notEmpty,
  Nullable,
  useResource,
  useToast,
} from "@tager/admin-services";
import {
  createTabErrorFinder,
  OptionType,
  TabType,
  TagerFormSubmitEvent,
  FormFooter,
  FormField,
  useSelectOptionsResource,
} from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";

import { FullBasisProductType, ProductSearchType } from "@/typings/model";
import {
  createBasisProduct,
  getBasisProduct,
  getProductSearchList,
  updateBasisProduct,
} from "@/services/requests";
import {
  convertFormValuesToBasisProductCreationPayload,
  convertFormValuesToBasisProductUpdatePayload,
  convertBasisProductToFormValues,
  FormValues,
} from "@/views/BasisProductForm/BasisProductForm.helpers";
import { getBasisProductFormUrl, getBasisProductListUrl } from "@/utils/paths";
import { getWebsiteOrigin } from "@/utils/common";
import SettingBlock from "@/views/BasisProductForm/components/SettingBlock.vue";
import ProductTable from "@/views/BasisProductForm/components/ProductTable.vue";

export default defineComponent({
  name: "BasisProductForm",
  components: { SettingBlock, ProductTable, FormFooter, FormField, Page },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const productId = computed<string>(() => route.params.productId as string);

    const isCreation = computed<boolean>(() => productId.value === "create");

    const {
      loading: isProductLoading,
      options: productSearchOptionList,
      noOptionsMessage: noProductOptionsMessage,
      handleSearchQueryChange: handleProductSearchQueryChange,
    } = useSelectOptionsResource<ProductSearchType, OptionType<number>>({
      fetchEntityList: getProductSearchList,
      resourceName: "Product search list",
      convertEntityToOption: (brand) => ({
        value: brand.id,
        label: brand.fullName,
      }),
    });

    const [
      fetchBasisProduct,
      { data: basisProduct, loading: isBasisProductLoading },
    ] = useResource<Nullable<FullBasisProductType>>({
      fetchResource: () => getBasisProduct(productId.value),
      initialValue: null,
      resourceName: "basisProduct",
    });

    onMounted(() => {
      if (isCreation.value) {
        return;
      }
      fetchBasisProduct();
    });

    watch(productId, () => {
      fetchBasisProduct();
    });

    const values = ref<FormValues>(
      convertBasisProductToFormValues(basisProduct.value)
    );

    const errors = ref<Record<string, string>>({});
    const isSubmitting = ref<boolean>(false);

    watch(basisProduct, () => {
      values.value = convertBasisProductToFormValues(basisProduct.value);
    });

    function submitForm(event: TagerFormSubmitEvent) {
      isSubmitting.value = true;

      const creationBody = convertFormValuesToBasisProductCreationPayload(
        values.value
      );
      const updateBody = convertFormValuesToBasisProductUpdatePayload(
        values.value
      );
      const requestPromise = isCreation.value
        ? createBasisProduct(creationBody)
        : updateBasisProduct(productId.value, updateBody);

      requestPromise
        .then((response) => {
          errors.value = {};

          if (event.type === "create") {
            router.push(
              getBasisProductFormUrl({ productId: response.data.id })
            );
          }
          if (event.type === "create_exit" || event.type === "save_exit") {
            router.push(getBasisProductListUrl());
          }
          if (event.type === "create_create-another") {
            values.value = convertBasisProductToFormValues(null);
          }

          toast.show({
            variant: "success",
            title: "Success",
            body: isCreation.value
              ? `Товар успешно добавлен`
              : "Товар успешно обновлен",
          });
        })
        .catch((error) => {
          console.error(error);
          errors.value = convertRequestErrorToMap(error);
          toast.show({
            variant: "danger",
            title: "Ошибка",
            body: isCreation.value
              ? `Ошибка при добавлении товара`
              : "Ошибка при обновлении товара",
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    }

    const isContentLoading = computed<boolean>(
      () => isBasisProductLoading.value
    );

    /** Tabs */

    const isHide = ref<boolean>(false);

    watch(
      () => values.value.params,
      (params, prevParams) => {
        if (prevParams.length) {
          isHide.value = true;
        }
      },
      { deep: true }
    );

    const tabList = computed<Array<TabType>>(() => {
      const hasErrors = createTabErrorFinder(errors.value);
      return [
        {
          id: "common",
          label: "Основная",
          hasErrors: hasErrors(["name"]),
        },
        {
          id: "properties",
          label: "Свойства",
          hasErrors: hasErrors([]),
        },
        !(isHide.value || !basisProduct.value?.products.length)
          ? {
              id: "products",
              label: "Товары",
              hasErrors: hasErrors([]),
            }
          : null,
      ].filter(notEmpty);
    });

    const selectedTabId = ref<string>(tabList.value[0].id);

    const pageTitle = computed<string>(() => {
      return isCreation.value
        ? `Добавить товар`
        : `Редактировать товар
        "${basisProduct.value ? basisProduct.value.name : ""}"`;
    });

    const pagePath = computed<string>(() => {
      return getWebsiteOrigin() + "/basisProducts/";
    });

    return {
      backButtonUrl: getBasisProductListUrl(),
      values,
      errors,
      isSubmitting,
      submitForm,
      isContentLoading,
      basisProduct,
      isCreation,
      pageTitle,
      tabList,
      selectedTabId,
      pagePath,
      isHide,

      productSearchOptionList,
      handleProductSearchQueryChange,
      noProductOptionsMessage,
      isProductLoading,
    };
  },
});
